import React, {FC, RefObject, useCallback, useRef, useState} from 'react';

import {KeyboardActionsEnum} from '../../../../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../../../../libs/shared/helpers/src/internationalization/react';
import {useToggle} from '../../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {
  AsyncHandler,
  Handler,
  RendererOf,
} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {ErrorWithData} from '../../../../../../../libs/shared/helpers/src/types/customErrors';
import {KeyboardEventHandlers} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {TriageActionsEnum} from '../../../../../../../srcCommon/inboxes/viewer/conversation/triageActionHelpers';
import {reportToBugsnag} from '../../../../../../../srcCommon/services/bugsnag/bugsnag';
import {BulkActionTypesEnum} from '../../../../../../helpers/bulkActionHelpers';
import {buildContactDisplayName} from '../../../../../../helpers/models/contactHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../../helpers/models/tagHelpers';
import {useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {VisualRecipientModel} from '../../../../../../models/api/visualRecipientModel';
import {HttpResponseError} from '../../../../../../services/http/httpError';
import {BulkActionsTrackingTypesEnum} from '../../../../../../services/tracking';
import {getHasLazyTeammatesDropdowns} from '../../../../../../store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../../store/panels/cells/context/cellsContext';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';
import {BasicDialog} from '../../../../../common/dialogs/basicDialog';
import {OkCancelDialog} from '../../../../../common/dialogs/okCancelDialog';
import {CreateTagRuleDropdownContainer} from '../../../../../common/dropdowns/rules/createTagRuleDropdownContainer';
import {Bold} from '../../../../../common/text/bold';
import {ConversationSummaryContainer} from '../../conversationSummary/conversationSummaryContainer';
import {ConversationSummarySectionsEnum} from '../../conversationSummary/conversationSummarySections';
import {AssignDropdownContainer} from '../../topbar/assign/assignDropdownContainer';
import {LazyAssignDropdownContainer} from '../../topbar/assign/lazyAssignDropdownContainer';
import {TriageTeamConfirmationDialogContainer} from '../../topbar/triageTeamConfirmationDialogContainer';
import {BulkActionConfirmationDialog} from '../bulk/bulkActionConfirmationDialog';
import {SnoozeDropdownContainer} from '../snooze/snoozeDropdownContainer';
import {ConversationsMoreDropdown, ConversationsMoreDropdownPublicProps} from './conversationsMoreDropdown';

/*
 * Props.
 */

interface ConversationsMoreCoordinatorProps extends ConversationsMoreDropdownPublicProps {
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  inboxesParameters: InboxesParameters;
  hasSnoozeItem?: boolean;
  hasSubscribeItem?: boolean;
  hasAssignItem?: boolean;
  hasStarItem?: boolean;

  onBlockRecipientClick: Handler;
  onReportPhishingClick: Handler;
  onMergeClick: AsyncHandler;
  onSubscribeClick: Handler;
  onUnsubscribeClick: Handler;
  onMarkAsRead: Handler;
  onMarkAsUnread: Handler;
  onBulkMarkAsRead: Handler;
  onBulkMarkAsUnread: Handler;
  onMarkAsSpam: Handler;
  onBulkMarkAsSpam: Handler;
  onStar: Handler;
  onUnstar: Handler;
  onBulkStar: Handler;
  onBulkUnstar: Handler;
  isBulkActionRequired: boolean;
  shouldShowSpamTeamAlert: boolean;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;

  renderButton: RendererOf<ConversationsMoreButtonProps>;
  isInMultiConversationsViewer?: boolean;
}

export interface ConversationsMoreButtonProps {
  onClick: Handler;
  hasHover: boolean;
  buttonRef?: RefObject<HTMLDivElement>;
}

/*
 * Component.
 */

export const ConversationsMoreCoordinator: FC<ConversationsMoreCoordinatorProps> = (props) => {
  /*
   * State.
   */

  const [isDropdownOpen, toggleIsDropdownOpen] = useToggle();
  const [isSnoozeDropdownOpen, toggleIsSnoozeDropdownOpen] = useToggle();
  const [isAssignDropdownOpen, toggleIsAssignDropdownOpen] = useToggle();
  const [isBlockRecipientConfirmationOpen, toggleIsBlockRecipientConfirmationOpen] = useToggle();
  const [isMergeConfirmationOpen, toggleIsMergeConfirmationOpen] = useToggle();
  const [mergeErrorCode, setMergeErrorCode] = useState<string>();
  const [isReportPhishingConfirmationOpen, toggleIsReportPhishingConfirmationOpen] = useToggle();
  const [conversationSummarySection, setConversationSummarySection] =
    useState<ConversationSummarySectionsEnum>();
  const [isCreateRuleOpen, toggleIsCreateRuleOpen] = useToggle();
  const [isSpamTeamAlertOpen, toggleIsSpamTeamAlertOpen] = useToggle();
  const [bulkActionConfirmationType, setBulkActionConfirmationType] = useState<BulkActionTypesEnum>();

  /*
   * Event handlers.
   */

  const {
    recipient,
    onMergeClick,
    onBlockRecipientClick,
    onReportPhishingClick,
    onBulkMarkAsRead,
    onBulkMarkAsUnread,
    onBulkMarkAsSpam,
    onBulkStar,
    onBulkUnstar,
    shouldShowSpamTeamAlert,
  } = props;

  const onMergeOkClick = useCallback(async () => {
    toggleIsMergeConfirmationOpen();

    try {
      await onMergeClick();
    } catch (error) {
      const errorCode = (error instanceof HttpResponseError && error.code) || 'unknown';
      setMergeErrorCode(errorCode);
    }
  }, [onMergeClick, toggleIsMergeConfirmationOpen]);

  const onBlockRecipientOkClick = useCallback(() => {
    toggleIsBlockRecipientConfirmationOpen();
    onBlockRecipientClick();
  }, [onBlockRecipientClick, toggleIsBlockRecipientConfirmationOpen]);

  const onReportPhishingOkClick = useCallback(() => {
    toggleIsReportPhishingConfirmationOpen();
    onReportPhishingClick();
  }, [onReportPhishingClick, toggleIsReportPhishingConfirmationOpen]);

  const onBulkConfirmation = useCallback(() => {
    setBulkActionConfirmationType(undefined);

    switch (bulkActionConfirmationType) {
      case BulkActionTypesEnum.MARK_READ: {
        onBulkMarkAsRead();
        return;
      }
      case BulkActionTypesEnum.MARK_UNREAD: {
        onBulkMarkAsUnread();
        return;
      }
      case BulkActionTypesEnum.SPAM: {
        onBulkMarkAsSpam();
        return;
      }
      case BulkActionTypesEnum.STAR: {
        onBulkStar();
        return;
      }
      case BulkActionTypesEnum.UNSTAR: {
        onBulkUnstar();
        return;
      }
      default:
        reportToBugsnag(
          new ErrorWithData('Unexpected action type', {
            bulkActionConfirmationType,
          }),
        );
    }
  }, [
    bulkActionConfirmationType,
    onBulkMarkAsRead,
    onBulkMarkAsUnread,
    onBulkMarkAsSpam,
    onBulkStar,
    onBulkUnstar,
  ]);

  const {isBulkActionRequired, onMarkAsRead, onMarkAsUnread, onMarkAsSpam, onStar, onUnstar} = props;
  const onMarkAsReadClick = useCallback(() => {
    if (isBulkActionRequired) {
      // Confirm for bulk action.
      setBulkActionConfirmationType(BulkActionTypesEnum.MARK_READ);
      return;
    }

    // Call immediately for normal action.
    onMarkAsRead();
  }, [isBulkActionRequired, onMarkAsRead]);

  const onMarkAsUnreadClick = useCallback(() => {
    if (isBulkActionRequired) {
      // Confirm for bulk action.
      setBulkActionConfirmationType(BulkActionTypesEnum.MARK_UNREAD);
      return;
    }

    // Call immediately for normal action.
    onMarkAsUnread();
  }, [isBulkActionRequired, onMarkAsUnread]);

  const onMarkAsSpamClick = useCallback(() => {
    if (isBulkActionRequired) {
      // Confirm for bulk action.
      setBulkActionConfirmationType(BulkActionTypesEnum.SPAM);
      return;
    }

    if (shouldShowSpamTeamAlert) {
      toggleIsSpamTeamAlertOpen();
      return;
    }

    // Call immediately for normal action.
    onMarkAsSpam();
  }, [isBulkActionRequired, onMarkAsSpam, shouldShowSpamTeamAlert, toggleIsSpamTeamAlertOpen]);

  const onStarClick = useCallback(() => {
    if (isBulkActionRequired) {
      // Confirm for bulk action.
      setBulkActionConfirmationType(BulkActionTypesEnum.STAR);
      return;
    }

    // Call immediately for normal action.
    onStar();
  }, [isBulkActionRequired, onStar]);

  const onUnstarClick = useCallback(() => {
    if (isBulkActionRequired) {
      // Confirm for bulk action.
      setBulkActionConfirmationType(BulkActionTypesEnum.UNSTAR);
      return;
    }

    // Call immediately for normal action.
    onUnstar();
  }, [isBulkActionRequired, onUnstar]);

  /*
   * Keyboard shortcuts.
   */

  // If a single conversation is selected, register the "Conversation Summary" shortcut.
  const maybeRegisterConversationSummaryShortcut = props.conversations.length === 1 && {
    [KeyboardActionsEnum.INFORMATION]: () =>
      setConversationSummarySection(ConversationSummarySectionsEnum.DETAILS),
  };

  const keyHandlers: KeyboardEventHandlers = {
    ...maybeRegisterConversationSummaryShortcut,
    [KeyboardActionsEnum.COLLAPSE_ALL]: (!props.isAllCollapsed && props.onCollapseClick) || undefined,
    [KeyboardActionsEnum.EXPAND_ALL]: (!props.isAllExpanded && props.onExpandClick) || undefined,
  };

  /*
   * Feature flags.
   */

  const hasLazyTeammatesDropdowns = useAppSelector(getHasLazyTeammatesDropdowns);

  /*
   * Dropdown.
   */

  const buttonRef = useRef<HTMLDivElement>(null);
  const renderDropdown = () => (
    <ConversationsMoreDropdown
      {...props}
      anchor={buttonRef}
      onRequestClose={toggleIsDropdownOpen}
      onSnoozeClick={props.hasSnoozeItem ? toggleIsSnoozeDropdownOpen : undefined}
      onSubscribeClick={props.hasSubscribeItem ? props.onSubscribeClick : undefined}
      onUnsubscribeClick={props.hasSubscribeItem ? props.onUnsubscribeClick : undefined}
      onStarClick={props.hasStarItem ? onStarClick : undefined}
      onUnstarClick={props.hasStarItem ? onUnstarClick : undefined}
      onAssignClick={props.hasAssignItem ? toggleIsAssignDropdownOpen : undefined}
      onMergeClick={toggleIsMergeConfirmationOpen}
      onReportPhishingClick={toggleIsReportPhishingConfirmationOpen}
      onBlockRecipientClick={toggleIsBlockRecipientConfirmationOpen}
      onOpenConversationSummaryClick={() =>
        setConversationSummarySection(ConversationSummarySectionsEnum.DETAILS)
      }
      onOpenConversationAttachmentsClick={() =>
        setConversationSummarySection(ConversationSummarySectionsEnum.ATTACHMENTS)
      }
      onCreateRuleClick={toggleIsCreateRuleOpen}
      onMarkAsReadClick={onMarkAsReadClick}
      onMarkAsUnreadClick={onMarkAsUnreadClick}
      onMarkAsSpamClick={onMarkAsSpamClick}
      canExpandAll={props.canExpandAll}
      isAllExpanded={props.isAllExpanded}
      isAllCollapsed={props.isAllCollapsed}
      onExpandClick={props.onExpandClick}
      onCollapseClick={props.onCollapseClick}
    />
  );

  /*
   * Move.
   */

  /*
   * Snooze.
   */

  const maybeRenderSnoozeDropdown = () => {
    if (!isSnoozeDropdownOpen) {
      return null;
    }

    return (
      <SnoozeDropdownContainer
        anchor={buttonRef}
        onRequestClose={toggleIsSnoozeDropdownOpen}
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        inboxesParameters={props.inboxesParameters}
        onInterfaceEvent={props.onInterfaceEvent}
        shouldUntether
        shouldSkipApplyMaxSize
      />
    );
  };

  /*
   * Snooze.
   */

  const maybeRenderAssignDropdown = () => {
    if (!isAssignDropdownOpen) {
      return null;
    }

    if (hasLazyTeammatesDropdowns) {
      return (
        <LazyAssignDropdownContainer
          anchor={buttonRef}
          onRequestClose={toggleIsAssignDropdownOpen}
          cellsContext={props.cellsContext}
          conversations={props.conversations}
          isBulkActionRequired={props.isBulkActionRequired}
          panelId={props.panelId}
          inboxesParameters={props.inboxesParameters}
          onInterfaceEvent={props.onInterfaceEvent}
          placement="top-end"
          interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.MULTI_SELECT_MODAL}
          shouldUntether
          shouldSkipApplyMaxSize
        />
      );
    }

    return (
      <AssignDropdownContainer
        anchor={buttonRef}
        onRequestClose={toggleIsAssignDropdownOpen}
        cellsContext={props.cellsContext}
        conversations={props.conversations}
        isBulkActionRequired={props.isBulkActionRequired}
        panelId={props.panelId}
        inboxesParameters={props.inboxesParameters}
        onInterfaceEvent={props.onInterfaceEvent}
        placement="top-end"
        interfaceEventSource={ViewerDropdownInterfaceEventSourcesEnum.MULTI_SELECT_MODAL}
        shouldUntether
        shouldSkipApplyMaxSize
      />
    );
  };

  /*
   * Merge.
   */

  const renderMergeConfirmation = () => (
    <OkCancelDialog
      title={<Trans id="conversation-merge-title">Merge conversations</Trans>}
      okButtonLabel={<Trans id="conversation-merge-ok">Merge</Trans>}
      onOkClick={onMergeOkClick}
      onRequestClose={toggleIsMergeConfirmationOpen}
    >
      <Trans id="conversation-merge-content">
        This will group all messages and comments in a single conversation. This action cannot be undone.
      </Trans>
    </OkCancelDialog>
  );

  const renderMergeError = (errorCode: string) => (
    <BasicDialog
      title={<Trans id="conversation-merge-error">Merge failed</Trans>}
      onRequestClose={() => setMergeErrorCode(undefined)}
    >
      {renderMergeErrorMessage(errorCode)}
    </BasicDialog>
  );

  /*
   * Block recipient.
   */

  const renderBlockRecipientConfirmation = () => {
    if (!recipient) {
      return null;
    }

    return (
      <BlockRecipientConfirmationDialog
        onOkClick={onBlockRecipientOkClick}
        onRequestClose={toggleIsBlockRecipientConfirmationOpen}
        recipient={recipient}
      />
    );
  };

  /*
   * Report phishing.
   */

  const renderReportPhishingConfirmation = () => (
    <ReportPhishingConfirmationDialog
      onOkClick={onReportPhishingOkClick}
      onRequestClose={toggleIsReportPhishingConfirmationOpen}
    />
  );

  /*
   * Conversation Summary.
   */

  const renderConversationSummary = () => {
    const {onlyConversation} = props;
    if (!onlyConversation) {
      return null;
    }

    return (
      <ConversationSummaryContainer
        conversation={onlyConversation}
        onRequestClose={() => setConversationSummarySection(undefined)}
        teammateId={props.cellsContext.teammateId}
        initiallyExpandedSection={conversationSummarySection}
        panelId={props.panelId}
      />
    );
  };

  /*
   * Rule.
   */

  const renderCreateRule = () => {
    const conversation = props.conversations && props.conversations.length && props.conversations[0];

    return (
      <CreateTagRuleDropdownContainer
        anchor={buttonRef}
        onRequestClose={toggleIsCreateRuleOpen}
        panelId={props.panelId}
        conversationIds={[conversation && conversation.id]}
      />
    );
  };

  /*
   * Spam team alert.
   */

  const renderSpamTeamDialog = () => (
    <TriageTeamConfirmationDialogContainer
      cellsContext={props.cellsContext}
      conversations={props.conversations}
      onConfirmClick={props.onMarkAsSpam}
      onRequestClose={toggleIsSpamTeamAlertOpen}
      action={TriageActionsEnum.SPAM}
    />
  );

  /*
   * Bulk action.
   */

  const maybeRenderBulkActionConfirmation = () => {
    if (!bulkActionConfirmationType) {
      return null;
    }

    return (
      <BulkActionConfirmationDialog
        onConfirmClick={onBulkConfirmation}
        onRequestClose={() => setBulkActionConfirmationType(undefined)}
        actionType={bulkActionConfirmationType}
      />
    );
  };

  /*
   * Button.
   */

  return (
    <KeyboardShortcuts handlers={keyHandlers}>
      {props.renderButton({
        onClick: toggleIsDropdownOpen,
        buttonRef,
        hasHover: isDropdownOpen || isCreateRuleOpen || isSnoozeDropdownOpen || isAssignDropdownOpen,
      })}
      {isDropdownOpen && renderDropdown()}
      {maybeRenderSnoozeDropdown()}
      {maybeRenderAssignDropdown()}
      {isBlockRecipientConfirmationOpen && renderBlockRecipientConfirmation()}
      {isMergeConfirmationOpen && renderMergeConfirmation()}
      {mergeErrorCode && renderMergeError(mergeErrorCode)}
      {isReportPhishingConfirmationOpen && renderReportPhishingConfirmation()}
      {conversationSummarySection && renderConversationSummary()}
      {isCreateRuleOpen && renderCreateRule()}
      {isSpamTeamAlertOpen && renderSpamTeamDialog()}
      {maybeRenderBulkActionConfirmation()}
    </KeyboardShortcuts>
  );
};

/*
 * Localization.
 */

function renderMergeErrorMessage(errorCode: string) {
  switch (errorCode) {
    case 'too_many_messages':
    case 'too_many_activities':
      return <Trans id="merge-error-toobig">These conversations are too big to be merged.</Trans>;

    case 'has_drafts':
      return <Trans id="merge-error-drafts">Some conversations contain drafts.</Trans>;

    case 'multiple_message_types':
      return <Trans id="merge-error-messagetypes">Some messages have different types.</Trans>;

    default:
      return <Trans id="merge-error-unknown">These conversations could not be merged.</Trans>;
  }
}

export const ReportPhishingConfirmationDialog: FC<{onOkClick: Handler; onRequestClose: Handler}> = (
  props,
) => (
  <OkCancelDialog
    title={<Trans id="conversation-reportphishing-title">Report phishing</Trans>}
    okButtonLabel={<Trans id="conversation-reportphishing-ok">Report</Trans>}
    onOkClick={props.onOkClick}
    onRequestClose={props.onRequestClose}
  >
    <Trans id="conversation-reportphishing-content">
      Reporting this conversation as a phishing attack will send a message to the Front security team and help
      prevent other attacks in the future.
    </Trans>
  </OkCancelDialog>
);

export const BlockRecipientConfirmationDialog: FC<{
  recipient: VisualRecipientModel;
  onOkClick: Handler;
  onRequestClose: Handler;
}> = (props) => {
  const recipientName = buildContactDisplayName(props.recipient);

  return (
    <OkCancelDialog
      title={<Trans id="conversation-actions-blocksender-title">Block sender</Trans>}
      okButtonLabel={<Trans id="conversation-actions-blocksender-ok">Block</Trans>}
      onOkClick={props.onOkClick}
      onRequestClose={props.onRequestClose}
    >
      <Trans id="conversation-actions-blocksender-content">
        You are about to block <Bold>{recipientName}</Bold>.
        <br />
        Future messages will go to the Spam folder.
      </Trans>
    </OkCancelDialog>
  );
};
